import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import ResourceList from "../../../components/resourceList/ResourceList";
import Button from "../../../components/button/Button";
import styles from "./CompanyDetailScreen.module.scss";
import classNames from "classnames";
import moment from "moment";
import {
  fetchBillingInvoices,
} from "../../../store/actions/admin/billingInvoices";
import billingInvoicesService from "../../../apis/services/admin/billingInvoicesService";
import taxPayersService from "../../../apis/services/admin/taxPayersService";

const FISCAL_DEVICES_HEADER = [
  { label: "IME", key: "name", sortable: false },
  { label: "ADRESA", key: "address", sortable: false },
  { label: "ONLINE", key: "lastOnlineAt", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const VENDING_MACHINES_HEADER = [
  { label: "IME", key: "name", sortable: false },
  { label: "SERIJSKI BROJ", key: "serialNumber", sortable: false },
  { label: "VERZIJA", key: "version", sortable: false },
  { label: "ONLINE", key: "lastOnlineAt", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const INVOICES_HEADER = [
  { label: "BROJ FAKTURE", key: "invoiceNumber", sortable: false },
  { label: "PERIOD", sortable: false },
  { label: "IZDAT", key: "issueDate", sortable: false },
  { label: "ROK", key: "dueDate", sortable: false },
  { label: "STATUS", key: "status", sortable: false },
  { label: "PLAĆENO", key: "isPaid", sortable: false },
  { label: "IZNOS", key: "total", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const SCHEMAS_HEADER = [
  { label: "NAZIV", key: "name", sortable: false },
  { label: "FAKTURISKA KOMPANIJA", key: "billingCompany", sortable: false },
  { label: "TIP PERIODA", key: "billingPeriodType", sortable: false },
  { label: "AKTIVNA", key: "isActive", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const USERS_HEADER = [
  { label: "IME", key: "name", sortable: false },
  { label: "EMAIL", key: "email", sortable: false },
  { label: "TELEFON", key: "phone", sortable: false },
  { label: "ULOGA", key: "role", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const CompanyDetailScreen = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("overview");
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);

  const [fiscalDevices, setFiscalDevices] = useState([]);
  const [vendingControllers, setVendingControllers] = useState([]);
  const [users, setUsers] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [billingCompanies, setBillingCompanies] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState(null);

  const invoices = useSelector((state) => state.billingInvoices.invoices);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    loadCompanyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (company) {
      loadInvoices();
      loadInvoiceStats();
      // Schemas are now loaded from the API response, so we don't need to call loadSchemas
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const loadInvoiceStats = async () => {
    if (!company) return;
    try {
      const res = await billingInvoicesService.getBillingInvoicesStats({ companyId: company.id });
      if (!isMountedRef.current) return;
      setInvoiceStats(res?.data || null);
    } catch (e) {
      if (!isMountedRef.current) return;
      setInvoiceStats(null);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      if (!isMountedRef.current) return;
      setLoading(true);
      const response = await taxPayersService.fetchCompanyInfo(id);
      if (!isMountedRef.current) return;
      
      const companyData = response.data?.company;
      const fiscalDevicesData = response.data?.fiscalDevices || [];
      const vendingControllersData = response.data?.vendingControllers || [];
      const usersData = response.data?.company?.users || [];
      const summaryData = response.data?.summary || null;
      const billingCompaniesData = response.data?.billingCompanies || [];
      const invoiceSchemasData = response.data?.invoiceSchemas || [];
      
      if (companyData) {
        setCompany(companyData);
        setFiscalDevices(fiscalDevicesData);
        setVendingControllers(vendingControllersData);
        setUsers(usersData);
        setSummary(summaryData);
        setBillingCompanies(billingCompaniesData);
        setSchemas(invoiceSchemasData);
      } else {
        toast.error("Kompanija nije pronađena");
        history.push("/taxpayers");
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error("Error loading company info:", error);
      toast.error("Greška pri učitavanju kompanije");
      history.push("/taxpayers");
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const loadInvoices = () => {
    if (company) {
      dispatch(fetchBillingInvoices(1, 20, company.id, null, "", null, null, null));
    }
  };

  const loadSchemas = async () => {
    // Schemas are now loaded from the API response, so we don't need to fetch them separately
    // This function is kept for backward compatibility but does nothing
    return;
  };

  const renderFiscalDeviceRow = (device, index) => {
    // Handle both timestamp (number) and ISO string formats
    const lastOnlineAt = device.lastOnlineAt
      ? (typeof device.lastOnlineAt === 'number'
          ? moment(device.lastOnlineAt)
          : moment(device.lastOnlineAt))
      : null;
    const isVirtualPFR = !device.salesDataController;
    const isOnline = isVirtualPFR
      ? true
      : lastOnlineAt && moment().diff(lastOnlineAt, "minutes") < 180;
    const lastOnline = lastOnlineAt
      ? lastOnlineAt.format("DD.MM.YYYY HH:mm")
      : "Nikad";

    return (
      <div
        className={classNames(styles.row, index % 2 === 0 ? styles.even : styles.odd)}
        key={device.id}
      >
        <div className={styles.cell}>
          <p>{device.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{device.businessUnit?.address?.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <div className={styles.statusWrapper}>
            <div className={classNames(styles.statusIndicator, isOnline ? styles.online : styles.offline)} />
            <p>{lastOnline}</p>
          </div>
        </div>
        <div className={styles.cell}>
          <button
            className={styles.viewButton}
            onClick={() => history.push(`/fiscal-devices/${device.id}`)}
          >
            Pregled
          </button>
        </div>
      </div>
    );
  };

  const renderVendingMachineRow = (controller, index) => {
    // Handle both timestamp (number) and ISO string formats
    const lastOnlineAt = controller.lastOnlineAt 
      ? (typeof controller.lastOnlineAt === 'number' 
          ? moment(controller.lastOnlineAt) 
          : moment(controller.lastOnlineAt))
      : null;
    const isOnline = lastOnlineAt && moment().diff(lastOnlineAt, "minutes") < 180;
    const lastOnline = lastOnlineAt
      ? lastOnlineAt.format("DD.MM.YYYY HH:mm")
      : "Nikad";

    return (
      <div
        className={classNames(styles.row, index % 2 === 0 ? styles.even : styles.odd)}
        key={controller.id}
      >
        <div className={styles.cell}>
          <p>{controller.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{controller.serialNumber || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{controller.version || "-"}</p>
        </div>
        <div className={styles.cell}>
          <div className={styles.statusWrapper}>
            <div className={classNames(styles.statusIndicator, isOnline ? styles.online : styles.offline)} />
            <p>{lastOnline}</p>
          </div>
        </div>
        <div className={styles.cell}>
          <button
            className={styles.viewButton}
            onClick={() => history.push(`/vending-controllers/${controller.id}`)}
          >
            Pregled
          </button>
        </div>
      </div>
    );
  };

  const renderInvoiceRow = (invoice, index) => {
    const issueDate = invoice.issueDate
      ? moment(invoice.issueDate).format("DD-MM-YYYY")
      : "-";
    const dueDate = invoice.dueDate
      ? moment(invoice.dueDate).format("DD-MM-YYYY")
      : "-";
    const periodStart = invoice.billingPeriodStart
      ? moment(invoice.billingPeriodStart).format("DD-MM-YYYY")
      : "-";
    const periodEnd = invoice.billingPeriodEnd
      ? moment(invoice.billingPeriodEnd).format("DD-MM-YYYY")
      : "-";
    const period = `${periodStart} - ${periodEnd}`;

    return (
      <div
        className={classNames(styles.row, index % 2 === 0 ? styles.even : styles.odd)}
        key={invoice.id}
      >
        <div className={styles.cell}>
          <p className={styles.invoiceNumber}>{invoice.invoiceNumber}</p>
        </div>
        <div className={styles.cell}>
          <p className={styles.period}>{period}</p>
        </div>
        <div className={styles.cell}>
          <p>{issueDate}</p>
        </div>
        <div className={styles.cell}>
          <p>{dueDate}</p>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, styles[invoice.status])}>
            {invoice.status === "draft" ? "Draft" : invoice.status === "sent" ? "Poslato" : invoice.status === "paid" ? "Plaćeno" : invoice.status === "overdue" ? "Zakasnelo" : "Otkazano"}
          </span>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, invoice.isPaid ? styles.paid : styles.unpaid)}>
            {invoice.isPaid ? "Da" : "Ne"}
          </span>
        </div>
        <div className={styles.cell}>
          <p className={styles.amount}>
            {typeof invoice.total === "number" ? invoice.total.toFixed(2) : (parseFloat(invoice.total) || 0).toFixed(2)} RSD
          </p>
        </div>
        <div className={styles.cell}>
          <button
            className={styles.viewButton}
            onClick={() => history.push(`/billing-invoices/${invoice.id}`)}
          >
            Pregled
          </button>
        </div>
      </div>
    );
  };

  // Data is already filtered by the API, so we can use it directly
  const filteredFiscalDevices = Array.isArray(fiscalDevices) ? fiscalDevices : [];
  const filteredVendingMachines = Array.isArray(vendingControllers) ? vendingControllers : [];
  const filteredUsers = Array.isArray(users) ? users : [];
  const filteredSchemas = schemas || [];

  const renderSchemaRow = (schema, index) => {
    return (
      <div
        className={classNames(styles.row, index % 2 === 0 ? styles.even : styles.odd)}
        key={schema.id}
      >
        <div className={styles.cell}>
          <p>{schema.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{schema.billingCompany?.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>
            {schema.defaultBillingPeriodType === "monthly" ? "Mesečno" :
             schema.defaultBillingPeriodType === "quarterly" ? "Kvartalno" :
             schema.defaultBillingPeriodType === "semi-annual" ? "Polugodišnje" :
             schema.defaultBillingPeriodType === "annual" ? "Godišnje" : "-"}
          </p>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, schema.isActive ? styles.active : styles.inactive)}>
            {schema.isActive ? "Da" : "Ne"}
          </span>
        </div>
        <div className={styles.cell}>
          <button
            className={styles.viewButton}
            onClick={() => history.push(`/invoice-schemas/${schema.id}`)}
            style={{ marginRight: "8px" }}
          >
            Pregled
          </button>
          <button
            className={styles.editButton}
            onClick={() => history.push(`/invoice-schemas/${schema.id}/edit`)}
          >
            Izmeni
          </button>
        </div>
      </div>
    );
  };

  const renderUserRow = (user, index) => {
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || "-";
    const roleLabel = user.role === "ADMIN" ? "Admin" : user.role === "USER" ? "Korisnik" : user.role || "-";

    return (
      <div
        className={classNames(styles.row, index % 2 === 0 ? styles.even : styles.odd)}
        key={user.id}
      >
        <div className={styles.cell}>
          <p>{fullName}</p>
        </div>
        <div className={styles.cell}>
          <p>{user.email || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{user.phone || "-"}</p>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, user.role === "ADMIN" ? styles.active : styles.inactive)}>
            {roleLabel}
          </span>
        </div>
        <div className={styles.cell}>
          <button
            className={styles.viewButton}
            onClick={() => {
              // Navigate to user detail page if it exists, or show a message
              toast.info("Detalji korisnika nisu dostupni");
            }}
          >
            Pregled
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className={styles.container}>
          <p>Učitavanje...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!company) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <button className={styles.backButton} onClick={() => history.goBack()}>
              ← Nazad
            </button>
            <h1 className={styles.title}>{company.name}</h1>
            <p className={styles.subtitle}>PIB: {company.PIB}</p>
          </div>
        </div>

        <div className={styles.tabs}>
          <button
            className={classNames(styles.tab, activeTab === "overview" && styles.active)}
            onClick={() => setActiveTab("overview")}
          >
            Pregled
          </button>
          <button
            className={classNames(styles.tab, activeTab === "fiscal-devices" && styles.active)}
            onClick={() => setActiveTab("fiscal-devices")}
          >
            Fiskalni uređaji ({filteredFiscalDevices.length})
          </button>
          <button
            className={classNames(styles.tab, activeTab === "vending-machines" && styles.active)}
            onClick={() => setActiveTab("vending-machines")}
          >
            Vending mašine ({filteredVendingMachines.length})
          </button>
          <button
            className={classNames(styles.tab, activeTab === "invoices" && styles.active)}
            onClick={() => setActiveTab("invoices")}
          >
            Fakture ({invoices?.data?.total || 0})
          </button>
          <button
            className={classNames(styles.tab, activeTab === "schemas" && styles.active)}
            onClick={() => setActiveTab("schemas")}
          >
            Šeme faktura ({filteredSchemas.length})
          </button>
          <button
            className={classNames(styles.tab, activeTab === "users" && styles.active)}
            onClick={() => setActiveTab("users")}
          >
            Korisnici ({filteredUsers.length})
          </button>
        </div>

        <div className={styles.content}>
          {activeTab === "overview" && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Osnovne informacije</h2>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <label>Naziv kompanije:</label>
                  <p>{company.name || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>PIB:</label>
                  <p>{company.PIB || "-"}</p>
                </div>
                {company.addressId && (
                  <div className={styles.infoItem}>
                    <label>ID adrese:</label>
                    <p>{company.addressId}</p>
                  </div>
                )}
              </div>

              {summary && (
                <>
                  <h2 className={styles.sectionTitle} style={{ marginTop: "32px" }}>Statistika</h2>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <label>Poslovni prostori:</label>
                      <p>{summary.totalBusinessUnits || 0}</p>
                    </div>
                    <div className={styles.infoItem}>
                      <label>Fiskalni uređaji:</label>
                      <p>{summary.totalFiscalDevices || 0}</p>
                    </div>
                    <div className={styles.infoItem}>
                      <label>Vending mašine:</label>
                      <p>{summary.totalVendingControllers || 0}</p>
                    </div>
                    <div className={styles.infoItem}>
                      <label>Korisnici:</label>
                      <p>{summary.totalUsers || 0}</p>
                    </div>
                    <div className={styles.infoItem}>
                      <label>Fakturiske kompanije:</label>
                      <p>{summary.totalBillingCompanies || 0}</p>
                    </div>
                    {summary.totalInvoiceSchemas !== undefined && (
                      <div className={styles.infoItem}>
                        <label>Šeme faktura:</label>
                        <p>{summary.totalInvoiceSchemas || 0}</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {billingCompanies && billingCompanies.length > 0 && (
                <>
                  <h2 className={styles.sectionTitle} style={{ marginTop: "32px" }}>Fakturiske kompanije</h2>
                  <div className={styles.billingCompaniesList}>
                    {billingCompanies.map((billingCompany) => (
                      <div key={billingCompany.id} className={styles.billingCompanyItem}>
                        <p className={styles.billingCompanyName}>{billingCompany.name || "-"}</p>
                        {billingCompany.taxId && (
                          <p className={styles.billingCompanyTaxId}>PIB: {billingCompany.taxId}</p>
                        )}
                        {billingCompany.contactFirstName && billingCompany.contactLastName && (
                          <p className={styles.billingCompanyContact}>
                            Kontakt: {billingCompany.contactFirstName} {billingCompany.contactLastName}
                          </p>
                        )}
                        {billingCompany.contactEmail && (
                          <p className={styles.billingCompanyEmail}>
                            <a href={`mailto:${billingCompany.contactEmail}`}>{billingCompany.contactEmail}</a>
                          </p>
                        )}
                        {billingCompany.contactPhone && (
                          <p className={styles.billingCompanyPhone}>
                            <a href={`tel:${billingCompany.contactPhone}`}>{billingCompany.contactPhone}</a>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "fiscal-devices" && (
            <div className={styles.section}>
              <ResourceList
                resource={{ 
                  data: Array.isArray(filteredFiscalDevices) ? filteredFiscalDevices : [], 
                  total: filteredFiscalDevices.length, 
                  page: 1, 
                  perPage: 100 
                }}
                onSort={() => {}}
                onLoadPage={() => {}}
                headerValues={FISCAL_DEVICES_HEADER}
                rowRender={renderFiscalDeviceRow}
                showLoader={false}
              />
            </div>
          )}

          {activeTab === "vending-machines" && (
            <div className={styles.section}>
              <ResourceList
                resource={{ 
                  data: Array.isArray(filteredVendingMachines) ? filteredVendingMachines : [], 
                  total: filteredVendingMachines.length, 
                  page: 1, 
                  perPage: 100 
                }}
                onSort={() => {}}
                onLoadPage={() => {}}
                headerValues={VENDING_MACHINES_HEADER}
                rowRender={renderVendingMachineRow}
                showLoader={false}
              />
            </div>
          )}

          {activeTab === "invoices" && (
            <div className={styles.section}>
              {invoiceStats && (
                <div className={styles.invoiceStats}>
                  <div className={classNames(styles.invoiceStatCard, styles.invoiceStatTotal)}>
                    <span className={styles.invoiceStatLabel}>Ukupno</span>
                    <span className={styles.invoiceStatValue}>
                      {(invoiceStats.totalAmount || 0).toLocaleString("sr-RS", { maximumFractionDigits: 2 })} RSD
                    </span>
                    <span className={styles.invoiceStatSub}>{invoiceStats.totalCount} faktura</span>
                  </div>
                  <div className={classNames(styles.invoiceStatCard, styles.invoiceStatPaid)}>
                    <span className={styles.invoiceStatLabel}>Plaćeno</span>
                    <span className={styles.invoiceStatValue}>
                      {(invoiceStats.paidAmount || 0).toLocaleString("sr-RS", { maximumFractionDigits: 2 })} RSD
                    </span>
                    <span className={styles.invoiceStatSub}>{invoiceStats.paidCount} faktura</span>
                  </div>
                  <div className={classNames(styles.invoiceStatCard, styles.invoiceStatDue)}>
                    <span className={styles.invoiceStatLabel}>Duguje</span>
                    <span className={styles.invoiceStatValue}>
                      {(invoiceStats.unpaidAmount || 0).toLocaleString("sr-RS", { maximumFractionDigits: 2 })} RSD
                    </span>
                    <span className={styles.invoiceStatSub}>{invoiceStats.unpaidCount} faktura</span>
                  </div>
                  <div className={classNames(styles.invoiceStatCard, styles.invoiceStatOverdue)}>
                    <span className={styles.invoiceStatLabel}>Zakasnelo</span>
                    <span className={styles.invoiceStatValue}>
                      {(invoiceStats.overdueAmount || 0).toLocaleString("sr-RS", { maximumFractionDigits: 2 })} RSD
                    </span>
                    <span className={styles.invoiceStatSub}>{invoiceStats.overdueCount} faktura</span>
                  </div>
                </div>
              )}
              <ResourceList
                resource={invoices.data || { data: [], total: 0, page: 1, perPage: 20 }}
                onSort={() => {}}
                onLoadPage={(page) => {
                  dispatch(fetchBillingInvoices(page, invoices.data?.perPage || 20, company.id, null, "", null, null, null));
                }}
                headerValues={INVOICES_HEADER}
                rowRender={renderInvoiceRow}
                showLoader={true}
              />
            </div>
          )}

          {activeTab === "schemas" && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Šeme faktura</h2>
                <Button
                  name="+ Nova šema"
                  onClick={() => history.push(`/invoice-schemas/new?companyId=${company.id}`)}
                />
              </div>
              <ResourceList
                resource={{ 
                  data: Array.isArray(filteredSchemas) ? filteredSchemas : [], 
                  total: filteredSchemas.length, 
                  page: 1, 
                  perPage: 100 
                }}
                onSort={() => {}}
                onLoadPage={() => {}}
                headerValues={SCHEMAS_HEADER}
                rowRender={renderSchemaRow}
                showLoader={false}
              />
            </div>
          )}

          {activeTab === "users" && (
            <div className={styles.section}>
              <ResourceList
                resource={{ 
                  data: Array.isArray(filteredUsers) ? filteredUsers : [], 
                  total: filteredUsers.length, 
                  page: 1, 
                  perPage: 100 
                }}
                onSort={() => {}}
                onLoadPage={() => {}}
                headerValues={USERS_HEADER}
                rowRender={renderUserRow}
                showLoader={false}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompanyDetailScreen;

